import { useCallback, useEffect, useRef, useState } from 'react'
import type { LogEntry } from './cardUtils'

interface CollapsibleLogCardProps {
  /** Array of structured log entries with timestamp and message */
  messages: LogEntry[]
  title?: string
  defaultExpanded?: boolean
  className?: string
  /** When true, hides cursor animation (use when step is complete) */
  isComplete?: boolean
}

/**
 * Single log entry row with timestamp and message
 */
function LogEntryRow({ entry }: { entry: LogEntry }) {
  return (
    <div className='flex items-start w-full'>
      {/* Timestamp column - fixed width, no wrap */}
      <div className='flex gap-[8px] items-start px-[16px] py-0 shrink-0'>
        <span className="font-['Geist_Mono'] font-semibold text-[12px] leading-normal text-[rgba(0,0,0,0.25)] whitespace-nowrap">
          {entry.timestamp || ''}
        </span>
      </div>
      {/* Message column - grows to fill, wraps naturally */}
      <div className='flex gap-[8px] items-start pl-[8px] pr-[16px] py-0 flex-1 min-w-0'>
        <span className="font-['Geist_Mono'] font-semibold text-[12px] leading-normal text-[rgba(0,0,0,0.45)] break-words">
          {entry.message}
        </span>
      </div>
    </div>
  )
}

/**
 * Log entry with a flashing cursor at the end
 * Plays typewriter effect once when message changes, then shows full message with blinking cursor
 */
function LogEntryRowWithCursor({ entry }: { entry: LogEntry }) {
  const [displayedText, setDisplayedText] = useState('')
  const [isTyping, setIsTyping] = useState(true)
  const currentIndexRef = useRef(0)
  const previousMessageRef = useRef<string | null>(null)

  // Reset typewriter when message changes
  useEffect(() => {
    if (previousMessageRef.current !== entry.message) {
      previousMessageRef.current = entry.message
      setDisplayedText('')
      setIsTyping(true)
      currentIndexRef.current = 0
    }
  }, [entry.message])

  // Typewriter animation - runs once per new message
  useEffect(() => {
    if (!isTyping) return

    if (currentIndexRef.current < entry.message.length) {
      const timer = setTimeout(() => {
        currentIndexRef.current += 1
        setDisplayedText(entry.message.slice(0, currentIndexRef.current))
      }, 20)
      return () => clearTimeout(timer)
    } else {
      setIsTyping(false)
    }
  }, [displayedText, entry.message, isTyping])

  return (
    <div className='flex items-start w-full'>
      <style>{`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
      {/* Timestamp column - fixed width, no wrap */}
      <div className='flex gap-[8px] items-start px-[16px] py-0 shrink-0'>
        <span className="font-['Geist_Mono'] font-semibold text-[12px] leading-normal text-[rgba(0,0,0,0.25)] whitespace-nowrap">
          {entry.timestamp || ''}
        </span>
      </div>
      {/* Message column - grows to fill, wraps naturally */}
      <div className='flex gap-[8px] items-start pl-[8px] pr-[16px] py-0 flex-1 min-w-0'>
        <span className="font-['Geist_Mono'] font-semibold text-[12px] leading-normal text-[rgba(0,0,0,0.45)] break-words">
          {isTyping ? displayedText : entry.message}
          <span className='inline-block w-[7px] h-[14px] bg-[rgba(0,0,0,0.45)] ml-[4px] align-text-bottom animate-[blink_0.9s_ease-in-out_infinite]' />
        </span>
      </div>
    </div>
  )
}

/**
 * Collapsible card displaying terminal-style agent progress logs
 * Matches the Figma design with purple-tinted background and separated timestamp display
 */
export function CollapsibleLogCard({
  messages,
  title = 'Working in progress',
  defaultExpanded = true,
  className = '',
  isComplete = false
}: CollapsibleLogCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [isAutoScroll, setIsAutoScroll] = useState(true)
  const logContainerRef = useRef<HTMLDivElement>(null)
  const lastMessageCountRef = useRef(0)
  const wasExpandedRef = useRef(defaultExpanded)

  // Auto-scroll to bottom when new messages arrive or on first expand
  useEffect(() => {
    if (!logContainerRef.current || !isExpanded) return

    const container = logContainerRef.current

    // Check if this is the first time expanding (or re-expanding after collapse)
    const justExpanded = !wasExpandedRef.current && isExpanded
    wasExpandedRef.current = isExpanded

    // Check if new messages were added
    const hasNewMessages = messages.length > lastMessageCountRef.current
    lastMessageCountRef.current = messages.length

    // Auto-scroll to bottom if:
    // 1. Just expanded (show latest content on expand)
    // 2. OR new messages arrived AND user is at/near bottom (isAutoScroll = true)
    if (justExpanded || (hasNewMessages && isAutoScroll)) {
      container.scrollTop = container.scrollHeight
    }
  }, [messages, isExpanded, isAutoScroll])

  // Reset state when collapsing to ensure fresh scroll on next expand
  useEffect(() => {
    if (!isExpanded) {
      wasExpandedRef.current = false
      setIsAutoScroll(true)
    }
  }, [isExpanded])

  const [showBottomFade, setShowBottomFade] = useState(false)
  const [showTopFade, setShowTopFade] = useState(false)

  const updateScrollState = useCallback(() => {
    if (!logContainerRef.current) return

    const container = logContainerRef.current
    const { scrollHeight, scrollTop, clientHeight } = container

    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    const isScrolledToBottom = distanceFromBottom < 10
    const isScrolledToTop = scrollTop < 10
    const hasOverflow = scrollHeight > clientHeight

    setIsAutoScroll(isScrolledToBottom)
    setShowBottomFade(hasOverflow && !isScrolledToBottom)
    setShowTopFade(hasOverflow && !isScrolledToTop)
  }, [])

  // Re-check overflow when messages change
  useEffect(() => {
    if (isExpanded) updateScrollState()
  }, [messages, isExpanded, updateScrollState])

  const handleScroll = useCallback(() => {
    updateScrollState()
  }, [updateScrollState])

  return (
    <div
      className={`flex flex-col gap-[4px] items-start bg-[rgba(132,111,225,0.1)] rounded-[20px] px-[12px] pt-[8px] ${isExpanded ? 'pb-[16px]' : 'pb-[8px]'} w-full ${className}`}
    >
      {/* Header with gradient title */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className='flex items-center justify-center h-[36px] overflow-hidden rounded-[12px]'
      >
        <div className='flex gap-[8px] items-center justify-center px-[16px] py-[8px]'>
          {/* Gradient text title */}
          <span
            className="font-['Geist'] font-medium text-[14px] leading-[22px] text-center whitespace-nowrap bg-clip-text"
            style={{
              WebkitTextFillColor: 'transparent',
              backgroundImage:
                'linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(90deg, #AB97FE 0%, #D3B6B8 82%, #A8CFF1 98.98%)'
            }}
          >
            {title}
          </span>
          <svg
            width={16}
            height={16}
            viewBox='0 0 16 16'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className={`transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`}
          >
            <path
              d='M4 6L8 10L12 6'
              stroke='rgba(0,0,0,0.25)'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </button>

      {/* Expanded Content - All log entries */}
      {isExpanded && (
        <div className='relative w-full'>
          <div
            ref={logContainerRef}
            onScroll={handleScroll}
            className='flex flex-col w-full pb-[8px] overflow-y-auto [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar]:block [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-track]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-[#C9C9C9] [&::-webkit-scrollbar-thumb]:rounded-[3px]'
            style={{ maxHeight: '200px' }}
          >
            {messages.length === 0 ? (
              <div className='flex items-center justify-center w-full py-[24px] text-[rgba(0,0,0,0.25)] text-[12px] font-semibold font-["Geist_Mono"]'>
                No progress messages yet...
              </div>
            ) : (
              messages.map((entry, idx) =>
                idx === messages.length - 1 && !isComplete ? (
                  <LogEntryRowWithCursor key={`log-${idx}`} entry={entry} />
                ) : (
                  <LogEntryRow key={`log-${idx}`} entry={entry} />
                )
              )
            )}
          </div>
          <div
            className='absolute top-0 left-0 right-0 h-[32px] pointer-events-none rounded-t-[20px] transition-opacity duration-300'
            style={{
              opacity: showTopFade ? 1 : 0,
              background: 'linear-gradient(to top, transparent, rgba(132,111,225,0.15))'
            }}
          />
          <div
            className='absolute bottom-0 left-0 right-0 h-[32px] pointer-events-none rounded-b-[20px] transition-opacity duration-300'
            style={{
              opacity: showBottomFade ? 1 : 0,
              background: 'linear-gradient(to bottom, transparent, rgba(132,111,225,0.15))'
            }}
          />
          <div
            className='absolute top-0 left-0 bottom-0 w-[16px] pointer-events-none rounded-l-[20px] transition-opacity duration-300'
            style={{
              opacity: showTopFade || showBottomFade ? 1 : 0,
              background: 'linear-gradient(to left, transparent, rgba(132,111,225,0.10))'
            }}
          />
          <div
            className='absolute top-0 right-0 bottom-0 w-[16px] pointer-events-none rounded-r-[20px] transition-opacity duration-300'
            style={{
              opacity: showTopFade || showBottomFade ? 1 : 0,
              background: 'linear-gradient(to right, transparent, rgba(132,111,225,0.10))'
            }}
          />
        </div>
      )}
    </div>
  )
}
